import {useState, useCallback, useEffect} from 'react';
import * as U from './util';
import {RemoteStreamPanel} from './remote-stream-panel';
import {LocalStreamPanel} from './local-stream-panel';
import * as aircoreMedia from '@aircore/aircore-media';
import {BUTTON_STYLE_MAIN, TEXT_INPUT_STYLE, PANEL_STYLE_MAIN} from './styles';
import {PublishCameraPayload, PublishDropdown, PublishScreensharePayload} from './publish-dropdown';
import {MediaDeviceSelector} from './media-device-selector';
import {VideoConfigSelector} from './video-config-selector';
import {Cog6ToothIcon, XCircleIcon} from '@heroicons/react/16/solid';
import {PineridgeChannel} from './pineridge-channel';
import loadingSpinner from './assets/loading.gif';
import {
  emitEvent,
  useEventHandler,
  Event,
  PineridgeChannelRemoteStreamPair,
  PineridgeChannelLocalStreamPair,
  UserChangeVideoEvent,
  UserPublishLocalEvent,
  UserScreenshareLocalEvent,
  UserSetAudioDeviceEvent,
} from './events';
import {VolumeSlider} from './volume-slider';

interface ChannelPanelProps {
  channel: PineridgeChannel;
}

export function ChannelPanel({channel}: ChannelPanelProps) {
  const [remoteStreams, setRemoteStreams] = useState<aircoreMedia.RemoteStream[]>([]);
  const [localStream, setLocalStream] = useState<aircoreMedia.LocalStream>();
  const [screenShareStream, setScreenShareStream] = useState<aircoreMedia.LocalStream>();
  const [needApproval, setNeedApproval] = useState(false);
  const [joinState, setJoinState] = useState(channel.mediaChannel.joinState);
  const [audioDeviceList, setAudioDeviceList] = useState<MediaDeviceInfo[]>([]);
  const [videoDeviceList, setVideoDeviceList] = useState<MediaDeviceInfo[]>([]);
  const [audioDevice, setAudioDevice] = useState(U.getDefaultMediaDevice());
  const [videoDevice, setVideoDevice] = useState(U.getDefaultMediaDevice());
  const [streamMap] = useState(new Map<aircoreMedia.RemoteStream, string>());
  const [videoConfig, setVideoConfig] = useState<string>();
  const [manualConstraints, setManualConstraints] = useState<MediaTrackConstraints>();
  const [additionalTags, setAdditionalTags] = useState<string[]>([]);
  const [isSatNearingExpiry, setIsSatNearingExpiry] = useState(false);
  const [panelBackgroundColor, setPanelBackgroundColor] = useState('gray-200');

  const joinChannel = useCallback(() => {
    emitEvent(Event.USER_JOIN_CHANNEL, channel);
  }, [channel]);

  const leaveChannel = useCallback(() => {
    emitEvent(Event.USER_LEAVE_CHANNEL, channel);
  }, [channel]);

  function handleChangeVideo() {
    if (!localStream) {
      U.appLog('Cannot change video before local stream is created');
      return;
    }

    const eventData: UserChangeVideoEvent = {
      stream: localStream,
      cameraParams: {videoDevice, videoConfig, manualConstraints},
      isAdd: false,
    };
    emitEvent(Event.USER_CHANGE_VIDEO, eventData);
  }

  const handleSetManualConstraints = useCallback((constraints: MediaTrackConstraints | undefined) => {
    setManualConstraints(constraints);
    setVideoConfig('Custom');
  }, []);

  const handleSetVideoConfig = useCallback((config: string | undefined) => {
    setManualConstraints(undefined);
    setVideoConfig(config);
  }, []);

  const handleAddVideo = useCallback(() => {
    if (!localStream) {
      U.appLog('Cannot add video before local stream is created');
      return;
    }

    const eventData: UserChangeVideoEvent = {
      stream: localStream,
      cameraParams: {videoDevice, videoConfig, manualConstraints},
      isAdd: true,
    };

    emitEvent(Event.USER_CHANGE_VIDEO, eventData);
  }, [localStream, videoDevice, videoConfig, manualConstraints]);

  const handleApprovePlayback = useCallback(() => {
    emitEvent(Event.USER_APPROVE_PLAYBACK);
    setNeedApproval(false);
  }, []);

  const fetchAudioDevices = useCallback(async () => {
    let devices: MediaDeviceInfo[] = [];

    try {
      devices = await aircoreMedia.Engine.sharedInstance.deviceManager.getAudioDevices();
    } catch (error) {
      U.appLog(`Failed to fetch audio devices: ${String(error)}`);
    }

    U.appLogO(
      `Queried for device list to populate audio source menu. Found ${devices.length} audio input devices:`,
      devices.map(d => {
        return {label: d.label, deviceId: d.deviceId};
      })
    );

    setAudioDeviceList(devices);

    const deviceIds = devices.map(device => device.deviceId);
    const defaultDevice = U.getDefaultMediaDevice();

    if (!deviceIds.includes(audioDevice.deviceId) || audioDevice.deviceId === defaultDevice.deviceId) {
      if (!deviceIds.includes(audioDevice.deviceId)) {
        U.appLog('Stale audio device detected, reverting to default');
      } else if (audioDevice.deviceId === 'default') {
        U.appLog('Updating default audio device');
      }

      setAudioDevice(defaultDevice);
    }
  }, [audioDevice]);

  const fetchVideoDevices = useCallback(async () => {
    let devices: MediaDeviceInfo[] = [];

    try {
      devices = await aircoreMedia.Engine.sharedInstance.deviceManager.getVideoDevices();
    } catch (error) {
      U.appLog(`Failed to fetch video devices: ${String(error)}`);
    }

    U.appLogO(
      `Queried for device list to populate video source menu. Found ${devices.length} video input devices:`,
      devices.map(d => {
        return {label: d.label, deviceId: d.deviceId};
      })
    );

    setVideoDeviceList(devices);

    const deviceIds = devices.map(device => device.deviceId);
    if (!deviceIds.includes(videoDevice.deviceId)) {
      U.appLog('Stale video device detected, reverting to default');
      setVideoDevice(devices[0] ?? U.getDefaultMediaDevice());
      if (localStream) {
        U.appLog('Muting video input for stale device');
        emitEvent(Event.USER_MUTE_LOCAL_VIDEO, localStream);
        handleChangeVideo();
      }
    }
  }, [localStream, videoDevice]);

  // Run when the component mounts to initialize the device lists
  useEffect(() => {
    void fetchAudioDevices();
    void fetchVideoDevices();
  }, []);

  useEffect(() => {
    U.appLog(`handleSetAudioDevice ${audioDevice.label}`);
    if (localStream) {
      const eventData: UserSetAudioDeviceEvent = {stream: localStream, device: audioDevice};
      emitEvent(Event.USER_SET_AUDIO_DEVICE, eventData);
    }
  }, [audioDevice]);

  useEffect(() => {
    if (isSatNearingExpiry && joinState !== aircoreMedia.ChannelJoinState.Terminated) {
      setPanelBackgroundColor('red-200');
    } else {
      switch (joinState) {
        case aircoreMedia.ChannelJoinState.Joining:
        case aircoreMedia.ChannelJoinState.Rejoining:
          setPanelBackgroundColor('yellow-100');
          break;
        case aircoreMedia.ChannelJoinState.Joined:
          setPanelBackgroundColor('green-100');
          break;
        case aircoreMedia.ChannelJoinState.Terminated:
          setPanelBackgroundColor('gray-400');
          break;
        default:
          setPanelBackgroundColor('gray-200');
      }
    }
  }, [joinState, isSatNearingExpiry]);

  function handleChangeAdditionalTags(event: React.FocusEvent<HTMLInputElement, Element>) {
    const tagsString = event.target.value ?? '';
    const tags = tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    setAdditionalTags(tags);
  }

  const handleChangeVolume = useCallback(
    (volume: number) => {
      // Only execute the change if the volume is valid
      if (!isNaN(volume)) {
        channel.mediaChannel.outputVolume = volume;
      }
    },
    [channel]
  );

  function handleClickChannelSettings() {
    emitEvent(Event.USER_EDIT_CHANNEL, channel);
  }

  function handleClickRemoveChannel() {
    emitEvent(Event.USER_REMOVE_CHANNEL, channel);
  }

  const handlePublishCamera = useCallback(
    (payload: PublishCameraPayload) => {
      const eventData: UserPublishLocalEvent = {
        channel,
        audioDevice,
        cameraParams: {videoDevice, videoConfig, manualConstraints},
        additionalTags,
        ...payload,
      };
      emitEvent(Event.USER_PUBLISH_LOCAL, eventData);
    },
    [channel, audioDevice, videoDevice, videoConfig, manualConstraints, additionalTags]
  );

  useEffect(() => {
    U.appLogO('manual constraints changed', manualConstraints ?? {});
  }, [manualConstraints]);

  const handlePublishScreenshare = useCallback(
    (payload: PublishScreensharePayload) => {
      const eventData: UserScreenshareLocalEvent = {
        channel,
        additionalTags,
        ...payload,
      };
      emitEvent(Event.USER_SCREENSHARE_LOCAL, eventData);
    },
    [channel, additionalTags]
  );

  useEventHandler(Event.PINERIDGE_AUTOSTART_BLOCKED, () => {
    setNeedApproval(true);
  });

  useEventHandler(Event.BIGSUR_REMOTE_STREAM_ADDED, (pair: PineridgeChannelRemoteStreamPair) => {
    const {pineridgeChannel: c, stream: newStream} = pair;
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }
    const streamId = newStream.streamId;
    streamMap.set(newStream, streamId);
    U.appLog(
      `Channel panel received remote stream add event for stream.userId: ${newStream.userId} generated streamId: ${streamId}`
    );
    // Note the filter is there in case the same stream is added twice.
    setRemoteStreams(prevRemoteStreams => [...prevRemoteStreams.filter(s => s !== newStream), newStream]);
  });

  useEventHandler(Event.BIGSUR_REMOTE_STREAM_REMOVED, (pair: PineridgeChannelRemoteStreamPair) => {
    const {pineridgeChannel: c, stream: remStream} = pair;
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }

    U.appLog(`Channel panel received remote stream remove event for stream.userId: ${remStream.userId}`);
    setRemoteStreams(prevRemoteStreams => [...prevRemoteStreams.filter(s => s !== remStream)]);
  });

  useEventHandler(
    Event.BIGSUR_LOCAL_STREAM_ADDED,
    (pair: PineridgeChannelLocalStreamPair) => {
      const {pineridgeChannel: c, stream: newStream} = pair;
      if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
        return;
      }
      if (newStream !== localStream) {
        U.appLog('Channel panel received local stream add event');
        setLocalStream(newStream);
      }
    },
    [localStream]
  );
  useEventHandler(Event.BIGSUR_LOCAL_STREAM_REMOVED, (pair: PineridgeChannelLocalStreamPair) => {
    const {pineridgeChannel: c} = pair;
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }

    U.appLog('Channel panel received local stream remove event');
    setLocalStream(undefined);
  });

  useEventHandler(
    Event.BIGSUR_SCREEN_SHARE_STREAM_ADDED,
    (pair: PineridgeChannelLocalStreamPair) => {
      const {pineridgeChannel: c, stream: newStream} = pair;
      if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
        return;
      }
      if (newStream !== screenShareStream) {
        U.appLog('Channel panel received screen share stream add event');
        setScreenShareStream(newStream);
      }
    },
    [screenShareStream]
  );

  useEventHandler(Event.BIGSUR_SCREEN_SHARE_STREAM_REMOVED, (pair: PineridgeChannelLocalStreamPair) => {
    const {pineridgeChannel: c} = pair;
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }

    U.appLog('Channel panel received screen share stream remove event');
    setScreenShareStream(undefined);
  });

  useEventHandler(
    Event.BIGSUR_CHANNEL_JOINED,
    (c: PineridgeChannel) => {
      if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
        return;
      }

      U.appLog(
        `Channel panel received channel joined event for channel: ${c.loggingId}, from joinState: ${joinState} to ${c.mediaChannel.joinState}`
      );
      // If we are not rejoining, clear the remote streams
      if (joinState !== aircoreMedia.ChannelJoinState.Rejoining) {
        setRemoteStreams([]);
      }
      setJoinState(c.mediaChannel.joinState);
    },
    [joinState]
  );
  useEventHandler(
    Event.BIGSUR_CHANNEL_LEFT,
    (c: PineridgeChannel) => {
      if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
        return;
      }

      U.appLog(`Channel panel received channel left event for channel: ${channel.loggingId}`);
      setJoinState(c.mediaChannel.joinState);
      setIsSatNearingExpiry(false);
    },
    [remoteStreams]
  );
  useEventHandler(Event.BIGSUR_CHANNEL_REJOINING, (c: PineridgeChannel) => {
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }

    U.appLog(`Channel panel received channel rejoining event for channel: ${channel.loggingId}`);
    setJoinState(c.mediaChannel.joinState);
  });
  useEventHandler(Event.BIGSUR_CHANNEL_JOINING, (c: PineridgeChannel) => {
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }

    U.appLog(`Channel panel received channel joining event for channel: ${channel.loggingId}`);
    setJoinState(c.mediaChannel.joinState);
  });

  useEventHandler(
    Event.BIGSUR_DEVICE_LIST_UPDATED,
    () => {
      U.appLog('Channel panel received device list update event');
      // TODO move this to a central location instead of making it channel specific
      void Promise.all([fetchAudioDevices(), fetchVideoDevices()]);
    },
    [localStream, audioDevice, videoDevice]
  );

  useEventHandler(Event.BIGSUR_CHANNEL_SAT_NEARING_EXPIRY, (c: PineridgeChannel) => {
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }
    setIsSatNearingExpiry(true);
  });

  useEventHandler(Event.PINERIDGE_CHANNEL_SAT_UPDATED, (c: PineridgeChannel) => {
    if (c.pineridgeChannelId !== channel.pineridgeChannelId) {
      return;
    }

    setIsSatNearingExpiry(false);
  });

  return (
    <>
      <div className={PANEL_STYLE_MAIN}>
        <div
          className={`flex flex-col w-full h-auto border-b-2 border-gray-500 text-sm pl-1 bg-${panelBackgroundColor}`}
        >
          <div className="w-full h-auto font-bold">
            <button className="float-left text-xl" onClick={handleClickChannelSettings}>
              {channel.loggingId} <Cog6ToothIcon className="inline h-5 w-5" />
            </button>
            <button className="float-right mt-1 mr-1" onClick={handleClickRemoveChannel}>
              <XCircleIcon className="inline h-8 w-8" />
            </button>
          </div>
          <div className="w-full h-auto">
            <span>
              joinState: {joinState} userId: {channel.mediaChannel.userId ?? 'unknown'} audioSource: {audioDevice.label}{' '}
              videoSource: {videoDevice.label} videoConfig: {videoConfig ?? 'Default'}
            </span>
          </div>
          <div className="w-full h-auto flex flex-row flex-wrap gap-2 my-1">
            <button className={BUTTON_STYLE_MAIN} onClick={handleApprovePlayback} hidden={!needApproval}>
              Approve Playback
            </button>
            <button className={BUTTON_STYLE_MAIN} onClick={joinChannel}>
              Join{' '}
              {(joinState === aircoreMedia.ChannelJoinState.Joining ||
                joinState === aircoreMedia.ChannelJoinState.Rejoining) && (
                <img className="inline h-4 w-4" src={loadingSpinner} />
              )}
            </button>
            <button className={BUTTON_STYLE_MAIN} onClick={leaveChannel}>
              Leave
            </button>
            {joinState !== aircoreMedia.ChannelJoinState.Joined || (localStream && screenShareStream) ? null : (
              <PublishDropdown
                isPublishingCamera={!!channel.mediaChannel.localStream}
                isPublishingScreenshare={!!channel.mediaChannel.screenShareStream}
                handlePublishCamera={handlePublishCamera}
                handlePublishScreenshare={handlePublishScreenshare}
              />
            )}
            <button className={BUTTON_STYLE_MAIN} onClick={handleChangeVideo} hidden={localStream === undefined}>
              Change Video
            </button>
            <MediaDeviceSelector
              title="Video Source"
              deviceList={videoDeviceList}
              onSelectDevice={setVideoDevice}
              loadDeviceList={fetchVideoDevices}
            />
            <VideoConfigSelector
              handleSetConfig={handleSetVideoConfig}
              handleSetManualConstraints={handleSetManualConstraints}
              initialManualConstraints={manualConstraints}
            />
            {joinState === aircoreMedia.ChannelJoinState.Joined && (
              <MediaDeviceSelector
                title="Audio Source"
                deviceList={audioDeviceList}
                onSelectDevice={setAudioDevice}
                loadDeviceList={fetchAudioDevices}
              />
            )}
            <VolumeSlider initialVolume={channel.mediaChannel.outputVolume} setVolume={handleChangeVolume} />
          </div>
          {joinState === aircoreMedia.ChannelJoinState.Terminated ? null : (
            <div style={{display: 'flex', alignItems: 'left', gap: '10px'}}>
              <span>Additional Publish Tags:</span>
              <input
                className={TEXT_INPUT_STYLE}
                type="text"
                defaultValue={''}
                onBlur={handleChangeAdditionalTags}
                placeholder="Enter comma-separated tags"
                style={{maxWidth: '250px', width: '100%'}}
              />
            </div>
          )}
          <br />
        </div>
        {localStream ? (
          <LocalStreamPanel stream={localStream} handleAddVideo={handleAddVideo} additionalTags={additionalTags} />
        ) : null}
        {screenShareStream ? (
          <LocalStreamPanel
            stream={screenShareStream}
            handleAddVideo={handleAddVideo}
            additionalTags={additionalTags}
          />
        ) : null}
        {remoteStreams.length ? (
          remoteStreams.map(stream => <RemoteStreamPanel key={streamMap.get(stream)} stream={stream} />)
        ) : (
          <div className="w-full h-auto text-sm text-gray-500">No remote streams</div>
        )}
      </div>
    </>
  );
}
